<template>
    <div class="min-h-screen bg-secondary-50 dark:bg-secondary-900">
        <!-- Simple Header -->
        <nav class="bg-white dark:bg-secondary-800 border-b border-secondary-200 dark:border-secondary-700 shadow-sm">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="flex justify-between items-center h-16">
                    <!-- Logo -->
                    <Link href="/dashboard" class="flex items-center space-x-3">
                        <div class="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                            <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                        </div>
                        <span class="text-xl font-semibold text-secondary-900 dark:text-white">
                            Margin Resolve
                        </span>
                    </Link>

                    <!-- Back to My Companies -->
                    <Link
                        href="/my-companies"
                        class="text-sm text-secondary-600 dark:text-secondary-400 hover:text-primary-600 dark:hover:text-primary-400 flex items-center space-x-1"
                    >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        <span>Back to My Companies</span>
                    </Link>
                </div>
            </div>
        </nav>

        <!-- Main Content -->
        <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <!-- Header -->
            <div class="text-center mb-12">
                <div class="inline-flex items-center justify-center w-16 h-16 bg-primary-100 dark:bg-primary-900 rounded-full mb-4">
                    <svg class="w-8 h-8 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                </div>
                <h1 class="text-3xl font-bold text-secondary-900 dark:text-white mb-2">
                    Create a New Company
                </h1>
                <p class="text-lg text-secondary-600 dark:text-secondary-400">
                    Set up your new company profile
                </p>
            </div>

            <!-- Enterprise Mode Notice -->
            <div v-if="!$page.props.deployment?.features?.companyCreationEnabled" class="rounded-md bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 p-6 mb-8">
                <div class="flex">
                    <div class="flex-shrink-0">
                        <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                        </svg>
                    </div>
                    <div class="ml-3">
                        <h3 class="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                            Company Creation Not Available
                        </h3>
                        <div class="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                            <p>Self-service company creation is currently disabled. Please contact your system administrator for assistance in setting up additional companies.</p>
                        </div>
                        <div class="mt-4">
                            <Link href="/my-companies" class="text-sm font-medium text-yellow-800 dark:text-yellow-200 hover:text-yellow-600 dark:hover:text-yellow-400">
                                Return to My Companies →
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            <form v-if="$page.props.deployment?.features?.companyCreationEnabled" @submit.prevent="handleSubmit" class="space-y-8">
                <!-- Company Information -->
                <div class="bg-white dark:bg-secondary-800 rounded-xl border border-secondary-200 dark:border-secondary-700 p-8 shadow-sm">
                    <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-6">
                        Company Information
                    </h2>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <!-- Company Name -->
                        <div class="md:col-span-2">
                            <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                                Company Name *
                            </label>
                            <input
                                v-model="form.company.name"
                                type="text"
                                required
                                class="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-secondary-700 dark:text-white"
                                placeholder="Acme Corporation"
                            />
                            <p v-if="errors['company.name']" class="mt-1 text-sm text-red-600">{{ errors['company.name'] }}</p>
                        </div>

                        <!-- Industry Type -->
                        <div>
                            <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                                Industry
                            </label>
                            <input
                                v-model="form.company.industry_type"
                                type="text"
                                class="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-secondary-700 dark:text-white"
                                placeholder="Technology, Healthcare, etc."
                            />
                        </div>

                        <!-- Company Size -->
                        <div>
                            <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                                Company Size
                            </label>
                            <select
                                v-model="form.company.company_size"
                                class="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-secondary-700 dark:text-white"
                            >
                                <option value="">Select size</option>
                                <option value="1-10">1-10 employees</option>
                                <option value="11-50">11-50 employees</option>
                                <option value="51-200">51-200 employees</option>
                                <option value="201-500">201-500 employees</option>
                                <option value="500+">500+ employees</option>
                            </select>
                        </div>

                        <!-- Address Line 1 -->
                        <div class="md:col-span-2">
                            <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                                Address Line 1 *
                            </label>
                            <input
                                v-model="form.company.address_line_1"
                                type="text"
                                required
                                class="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-secondary-700 dark:text-white"
                                placeholder="123 Main Street"
                            />
                            <p v-if="errors['company.address_line_1']" class="mt-1 text-sm text-red-600">{{ errors['company.address_line_1'] }}</p>
                        </div>

                        <!-- Address Line 2 -->
                        <div class="md:col-span-2">
                            <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                                Address Line 2
                            </label>
                            <input
                                v-model="form.company.address_line_2"
                                type="text"
                                class="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-secondary-700 dark:text-white"
                                placeholder="Suite 100"
                            />
                        </div>

                        <!-- City -->
                        <div>
                            <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                                City *
                            </label>
                            <input
                                v-model="form.company.city"
                                type="text"
                                required
                                class="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-secondary-700 dark:text-white"
                                placeholder="London"
                            />
                            <p v-if="errors['company.city']" class="mt-1 text-sm text-red-600">{{ errors['company.city'] }}</p>
                        </div>

                        <!-- State/Province -->
                        <div>
                            <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                                State/Province *
                            </label>
                            <input
                                v-model="form.company.state_province"
                                type="text"
                                required
                                class="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-secondary-700 dark:text-white"
                                placeholder="England"
                            />
                            <p v-if="errors['company.state_province']" class="mt-1 text-sm text-red-600">{{ errors['company.state_province'] }}</p>
                        </div>

                        <!-- Postal Code -->
                        <div>
                            <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                                Postal Code *
                            </label>
                            <input
                                v-model="form.company.postal_code"
                                type="text"
                                required
                                class="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-secondary-700 dark:text-white"
                                placeholder="SW1A 1AA"
                            />
                            <p v-if="errors['company.postal_code']" class="mt-1 text-sm text-red-600">{{ errors['company.postal_code'] }}</p>
                        </div>

                        <!-- Country -->
                        <div>
                            <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                                Country *
                            </label>
                            <select
                                v-model="form.company.country_id"
                                required
                                class="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-secondary-700 dark:text-white"
                            >
                                <option value="">Select country</option>
                                <option v-for="country in countries" :key="country.id" :value="country.id">
                                    {{ country.name }}
                                </option>
                            </select>
                            <p v-if="errors['company.country_id']" class="mt-1 text-sm text-red-600">{{ errors['company.country_id'] }}</p>
                        </div>

                        <!-- Phone -->
                        <div>
                            <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                                Phone
                            </label>
                            <input
                                v-model="form.company.phone"
                                type="tel"
                                class="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-secondary-700 dark:text-white"
                                placeholder="+44 20 1234 5678"
                            />
                        </div>

                        <!-- Website -->
                        <div>
                            <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                                Website
                            </label>
                            <input
                                v-model="form.company.website"
                                type="url"
                                class="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-secondary-700 dark:text-white"
                                placeholder="https://example.com"
                            />
                        </div>

                        <!-- Currency -->
                        <div>
                            <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                                Currency *
                            </label>
                            <select
                                v-model="form.company.currency_id"
                                required
                                class="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-secondary-700 dark:text-white"
                            >
                                <option value="">Select currency</option>
                                <option v-for="currency in currencies" :key="currency.id" :value="currency.id">
                                    {{ currency.code }} - {{ currency.name }} ({{ currency.symbol }})
                                </option>
                            </select>
                            <p v-if="errors['company.currency_id']" class="mt-1 text-sm text-red-600">{{ errors['company.currency_id'] }}</p>
                        </div>

                        <!-- Timezone -->
                        <div>
                            <label class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                                Timezone *
                            </label>
                            <select
                                v-model="form.company.timezone_id"
                                required
                                class="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-secondary-700 dark:text-white"
                            >
                                <option value="">Select timezone</option>
                                <option v-for="timezone in timezones" :key="timezone.id" :value="timezone.id">
                                    {{ timezone.display_name }} ({{ timezone.gmt_offset }})
                                </option>
                            </select>
                            <p v-if="errors['company.timezone_id']" class="mt-1 text-sm text-red-600">{{ errors['company.timezone_id'] }}</p>
                        </div>
                    </div>
                </div>

                <!-- Starter Plan Notice -->
                <div class="bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 rounded-xl border-2 border-primary-200 dark:border-primary-800 p-8 shadow-sm">
                    <div class="flex items-start space-x-4">
                        <div class="flex-shrink-0">
                            <div class="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center">
                                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                            </div>
                        </div>
                        <div class="flex-1">
                            <h2 class="text-2xl font-bold text-primary-900 dark:text-primary-100 mb-2">
                                Start for Free
                            </h2>
                            <p class="text-base text-primary-800 dark:text-primary-200 mb-4">
                                Your company will be created on our <span class="font-semibold">Starter Plan</span> at no cost. Get started immediately and explore all the features.
                            </p>
                            <div class="bg-white/50 dark:bg-gray-900/30 rounded-lg p-4 border border-primary-200 dark:border-primary-700">
                                <p class="text-sm text-primary-700 dark:text-primary-300 flex items-start">
                                    <svg class="w-5 h-5 text-primary-600 dark:text-primary-400 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                                    </svg>
                                    <span>You can upgrade to a paid plan at any time from your billing settings to unlock additional features and higher limits.</span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Submit Button -->
                <div class="flex justify-end space-x-4">
                    <Link
                        href="/my-companies"
                        class="px-6 py-3 border border-secondary-300 dark:border-secondary-600 text-secondary-700 dark:text-secondary-300 rounded-lg font-medium hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors"
                    >
                        Cancel
                    </Link>
                    <button
                        type="submit"
                        :disabled="processing"
                        class="px-8 py-3 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 disabled:from-secondary-400 disabled:to-secondary-500 text-white rounded-lg font-bold shadow-md hover:shadow-lg transition-all disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                        <svg v-if="!processing" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        <svg v-else class="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>{{ processing ? 'Creating Company...' : 'Create Company' }}</span>
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive } from 'vue';
import { Link, router } from '@inertiajs/vue3';
import { route } from '@/Utils/route';

// Props
const props = defineProps({
    plans: {
        type: Array,
        required: true
    },
    countries: {
        type: Array,
        required: true
    },
    currencies: {
        type: Array,
        required: true
    },
    timezones: {
        type: Array,
        required: true
    },
    user: {
        type: Object,
        required: true
    },
    errors: {
        type: Object,
        default: () => ({})
    }
});

// State
const processing = ref(false);
const form = reactive({
    company: {
        name: '',
        address_line_1: '',
        address_line_2: '',
        city: '',
        state_province: '',
        postal_code: '',
        country_id: '',
        currency_id: '',
        timezone_id: '',
        phone: '',
        website: '',
        industry_type: '',
        company_size: '',
    },
    plan_id: props.plans[0]?.id || null
});

// Note: plan_id is automatically set to the first plan (Starter plan) by default

// Methods
const handleSubmit = () => {
    if (processing.value) return;

    processing.value = true;

    router.post(route('company.store'), {
        company: form.company,
        plan_id: form.plan_id
    }, {
        onFinish: () => {
            processing.value = false;
        }
    });
};
</script>
